import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import { getUserDetails } from '../utils/orgName';
import ROLES from '../utils/roles';

export interface ProgramContext {
  groupId: string;
  orgId: string;
  coId: string;
  location: string;
  isPlatformAdmin: boolean;
}

/**
 * A program opened somewhere its route doesn't name it: the Program Editor
 * modal over the Programs board, whose URL has `admin` where the program id
 * would be. Everything under the provider reads this program.
 */
export const ProgramGroupIdContext = createContext<string | null>(null);

/**
 * Single source of truth for program-scoped IDs and location.
 *
 * Resolution hierarchy:
 *  - groupId:  ProgramGroupIdContext > route param `id` > Redux `selectedGroup.data.id`
 *  - orgId:    Platform Admin? route `orgId` / localStorage > JWT
 *  - coId:     Platform Admin? route `coId` > JWT
 *  - location: localStorage `orgLocation` > JWT metadata > 'us'
 */
export const useProgramContext = (): ProgramContext => {
  const {
    id: routeGroupId,
    orgId: routeOrgId,
    coId: routeCoId
  } = useParams<{ id: string; orgId: string; coId: string }>();

  const openedGroupId = useContext(ProgramGroupIdContext);
  const selectedGroupId = useAppSelector((s) => s.selectedGroup.data?.id);
  const isPlatformAdmin = localStorage.getItem('role') === ROLES.platAdmin;
  const { orgId: tokenOrgId, coId: tokenCoId, location: tokenLocation } = getUserDetails();
  const orgLocation = localStorage.getItem('orgLocation') || '';

  const groupId = openedGroupId || routeGroupId || selectedGroupId || '';
  const orgId = isPlatformAdmin
    ? routeOrgId || localStorage.getItem('orgId') || tokenOrgId
    : tokenOrgId;
  const coId = isPlatformAdmin ? routeCoId || tokenCoId : tokenCoId;
  const location = orgLocation || tokenLocation || 'us';

  return { groupId, orgId, coId, location, isPlatformAdmin };
};

/**
 * Non-hook equivalent for use outside React components (interceptors, api helpers).
 * Cannot resolve route params — only localStorage / JWT.
 */
export const getProgramContext = (): Omit<ProgramContext, 'groupId' | 'coId'> => {
  const { orgId, location: tokenLocation } = getUserDetails();
  const orgLocation = localStorage.getItem('orgLocation') || '';
  const isPlatformAdmin = localStorage.getItem('role') === ROLES.platAdmin;
  const storedOrgId = localStorage.getItem('orgId') || '';

  return {
    orgId: isPlatformAdmin && storedOrgId ? storedOrgId : orgId,
    location: orgLocation || tokenLocation || 'us',
    isPlatformAdmin
  };
};
